import React from "react";
import { Box, Typography, Button, Card, Grid } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RoomIcon from '@mui/icons-material/Room';
import PaymentsIcon from '@mui/icons-material/Payments';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';

export default function UpcomingEvent({ config }) {
  // If no upcoming events, show a placeholder
  if (!config || config.length === 0) {
    return (
      <Box
        sx={{
          py: 8,
          px: { xs: 2, md: 6 },
          background: "#FFFBF8",
          textAlign: "center"
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: "#BF0603",
            mb: 3,
            fontFamily: "Quicksand",
          }}
        >
          Upcoming Swayamvaram Events
        </Typography>
        <Card
          sx={{
            maxWidth: 600,
            mx: "auto",
            p: 5,
            borderRadius: "16px",
            border: "1px dashed rgba(191, 6, 3, 0.3)",
            backgroundColor: "#fff",
            boxShadow: "0 8px 30px rgba(0,0,0,0.02)"
          }}
        >
          <NotificationImportantIcon sx={{ fontSize: 60, color: "#D4AF37", mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: "Quicksand", mb: 1 }}>
            No Swayamvaram Meetups Scheduled Currently
          </Typography>
          <Typography variant="body2" sx={{ color: "#666", fontFamily: "Varela Round", mb: 3 }}>
            We are planning our next offline meetups. Stay tuned, or contact our relationship managers via WhatsApp to register your interest!
          </Typography>
          <Button
            variant="contained"
            startIcon={<WhatsAppIcon />}
            sx={{
              backgroundColor: "#25d366",
              fontWeight: 700,
              px: 3,
              py: 1.2,
              borderRadius: "30px",
              "&:hover": { backgroundColor: "#1ebe5d" },
            }}
            onClick={() => window.open("https://wa.me/918903791212?text=Hi, I am interested in upcoming Swayamvaram meetups. Please notify me.", "_blank")}
          >
            Inquire via WhatsApp
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        py: 8,
        px: { xs: 2, md: 6 },
        background: "#FFF8F2",
        position: "relative"
      }}
    >
      {/* Section Title */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: "#BF0603",
            fontFamily: "Quicksand",
            fontSize: { xs: "26px", md: "36px" }
          }}
        >
          Upcoming Swayamvaram Meetups
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "#666",
            fontFamily: "Varela Round",
            maxWidth: "600px",
            mx: "auto",
            mt: 1.5,
            fontSize: "0.95rem"
          }}
        >
          Participate in our offline matchmaking meetups to meet prospective partners and their families directly.
        </Typography>
      </Box>

      {/* Events List */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {config.map((event) => {
          // Default WhatsApp message
          const message = `I want to register for the event: ${event.titleEn || event.title}`;
          const whatsappLink = `https://wa.me/${event.whatsappNumber || "918903791212"}?text=${encodeURIComponent(message)}`;
          const mapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venue)}`;

          // Parsing date for visual calendar block (e.g. "12 July 2026")
          const dateParts = event.date.split(" ");
          const day = dateParts[0] || "01";
          const month = (dateParts[1] || "Jan").substring(0, 3).toUpperCase();
          const year = dateParts[2] || "";

          return (
            <Card
              key={event.id}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: "24px",
                boxShadow: "0 15px 40px rgba(191, 6, 3, 0.04)",
                border: "1px solid rgba(212, 175, 55, 0.2)",
                backgroundColor: "#fff",
                transition: "all 0.4s ease",
                "&:hover": {
                  boxShadow: "0 20px 45px rgba(191, 6, 3, 0.08)",
                  border: "1px solid rgba(212, 175, 55, 0.4)"
                }
              }}
            >
              <Grid container spacing={4} alignItems="center">
                {/* LEFT — IMAGE & DATE ACCENT */}
                <Grid item xs={12} md={5}>
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: "16px",
                      overflow: "hidden",
                      boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                      height: { xs: 260, md: 340 },
                      backgroundColor: "#f9dcc4"
                    }}
                  >
                    <img
                      src={event.bannerImage || event.image}
                      alt={event.titleEn || event.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />

                    {/* Desk Calendar Style Badge overlay */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 15,
                        left: 15,
                        width: "70px",
                        height: "75px",
                        backgroundColor: "#fff",
                        borderRadius: "12px",
                        overflow: "hidden",
                        boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        zIndex: 3
                      }}
                    >
                      <Box sx={{ backgroundColor: "#BF0603", color: "#fff", py: 0.3, fontWeight: 700, fontSize: "0.75rem", letterSpacing: 1 }}>
                        {month}
                      </Box>
                      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                        <Typography sx={{ fontWeight: 800, fontSize: "1.4rem", color: "#222", lineHeight: 1.1 }}>
                          {day}
                        </Typography>
                        {year && (
                          <Typography sx={{ fontSize: "0.65rem", color: "#666", fontWeight: 600 }}>
                            {year}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                {/* RIGHT — DETAILS */}
                <Grid item xs={12} md={7}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    {/* Event Tag */}
                    <Box
                      sx={{
                        backgroundColor: "rgba(191, 6, 3, 0.08)",
                        color: "#BF0603",
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        fontFamily: "Quicksand",
                        px: 2,
                        py: 0.5,
                        borderRadius: "20px",
                        width: "fit-content",
                        textTransform: "uppercase",
                        letterSpacing: 1
                      }}
                    >
                      Swayamvaram Meet
                    </Box>

                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: "#111",
                        fontFamily: "Quicksand",
                        fontSize: { xs: "20px", md: "24px" },
                        lineHeight: 1.3
                      }}
                    >
                      {event.titleEn || event.title}
                    </Typography>

                    {/* Details Rows */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <CalendarMonthIcon sx={{ color: "#BF0603", mt: 0.2 }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: "#888", display: "block" }}>Date</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#333", fontFamily: "Varela Round" }}>
                            {event.date}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <AccessTimeIcon sx={{ color: "#BF0603", mt: 0.2 }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: "#888", display: "block" }}>Timings</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#333", fontFamily: "Varela Round" }}>
                            {event.time}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <RoomIcon sx={{ color: "#BF0603", mt: 0.2 }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: "#888", display: "block" }}>Venue</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#333", fontFamily: "Varela Round", pr: 2 }}>
                            {event.venue}
                          </Typography>
                          <Button
                            size="small"
                            sx={{
                              color: "#BF0603",
                              fontWeight: 700,
                              textTransform: "none",
                              p: 0,
                              mt: 0.5,
                              fontFamily: "Quicksand",
                              fontSize: "0.8rem",
                              "&:hover": { backgroundColor: "transparent", textDecoration: "underline" }
                            }}
                            onClick={() => window.open(mapsLink, "_blank")}
                          >
                            Get Directions on Google Maps
                          </Button>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <PaymentsIcon sx={{ color: "#BF0603", mt: 0.2 }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: "#888", display: "block" }}>Entry Fee</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#D4AF37", fontFamily: "Varela Round", fontSize: "1.1rem" }}>
                            {event.fee} <span style={{ fontSize: "0.8rem", color: "#666", fontWeight: 500 }}>(per profile)</span>
                          </Typography>
                        </Box>
                      </Box>

                      {event.contactNumbers && event.contactNumbers.length > 0 && (
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                          <Box sx={{ color: "#BF0603", mt: 0.2, fontSize: "1.3rem" }}>📞</Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: "#888", display: "block" }}>Contact for Registration</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: "#333", fontFamily: "Varela Round", fontSize: "1rem" }}>
                              {event.contactNumbers.join(", ")}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {event.specialGuest && (
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                          <Box sx={{ color: "#D4AF37", mt: 0.2, fontSize: "1.2rem" }}>⭐</Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: "#888", display: "block" }}>Special Feature</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#333", fontFamily: "Varela Round" }}>
                              {event.specialGuest}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>

                    {/* Action buttons */}
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2 }}>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<WhatsAppIcon />}
                        sx={{
                          backgroundColor: "#25d366",
                          color: "#fff",
                          fontWeight: 700,
                          px: 4,
                          py: 1.5,
                          borderRadius: "30px",
                          fontFamily: "Quicksand",
                          textTransform: "none",
                          fontSize: "0.95rem",
                          boxShadow: "0 4px 15px rgba(37, 211, 102, 0.3)",
                          "&:hover": {
                            backgroundColor: "#1ebe5d",
                            boxShadow: "0 6px 20px rgba(37, 211, 102, 0.45)"
                          },
                        }}
                        onClick={() => window.open(whatsappLink, "_blank")}
                      >
                        Register via WhatsApp
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
}
